// Raw document types — shapes stored in JSON
export interface ScParticipation {
  reinsurer_id: string
  reinsurer_name: string
  short_name: string
  panel_order: number
  participation_pct: number
  treaty_pct: number | null
  edited: boolean
  note: string
}

export interface ScReportingRow {
  reporting_date: string
  insured_limit: number
  gross_premium: number
  gross_losses: number
  gross_recoveries: number
  gross_reserves: number
  payment_date: string | null
  comments: string
}

export interface ScCashflow {
  period: string
  year: number
  quarter: number
  expected_limit_eop: number | null
  expected_premium: number
  expected_loss: number
  expected_recovery: number
}

export interface ScProjection {
  vintage_id: string
  label: string
  as_of_date: string
  is_current: boolean
  is_synthetic: boolean
  cashflows: ScCashflow[]
}

export interface ScScenario {
  scenario: string
  return_period_years: number | null
  lifetime_premium: number
  lifetime_loss_dcc: number
  is_synthetic: boolean
}

export interface ScTermSheetRow {
  section: string
  label: string
  value: string
}

export interface ScTransaction {
  id: string
  client: string
  client_type: string
  year: number
  program: string
  name: string
  short_name: string
  structure: string
  basis: string | null
  reporting_level: string
  is_treaty?: boolean
  effective_date: string
  end_date: string | null
  legal_final_date: string | null
  currency: string
  limit: number
  limit_original_ccy?: number | null
  fx_rate_to_usd?: number
  brokerage_rate: number
  cession_pct: number
  ceding_commission_rate: number
  status: string
  recognition_method: string
  participations: ScParticipation[]
  projections: ScProjection[]
  reporting: ScReportingRow[]
  scenarios: ScScenario[]
  term_sheet_raw: ScTermSheetRow[]
  deal_file_name?: string | null
}

export interface ScDeal {
  id: string
  transaction_id: string
  name: string
  deal_vertical: string
  insured: string
  sponsor: string
  effective_date: string
  legal_final_date: string | null
  tenor_years: number | null
  currency: string
  limit: number
  limit_original_ccy?: number | null
  fx_rate_to_usd?: number
  cession_pct: number | null
  status: string
  participations: ScParticipation[]
  projections: ScProjection[]
  reporting: ScReportingRow[]
  scenarios: ScScenario[]
  term_sheet_raw: ScTermSheetRow[]
  deal_file_name?: string | null
}

// ---- Derived model types ----

/** The monitoring unit: an underlying deal (is_underlying=true) or a self-unit for 1:1 transactions */
export interface ScUnit {
  deal_id: string
  transaction_id: string
  is_underlying: boolean
  client: string
  client_type: string
  year: number
  program: string
  transaction_name: string
  name: string
  deal_vertical: string
  insured: string
  sponsor: string
  effective_date: string
  legal_final_date: string | null
  tenor_years: number | null
  currency: string
  limit: number
  limit_original_ccy?: number | null
  fx_rate_to_usd?: number
  cession_pct: number
  status: string
  placed_share: number
  brokerage_rate: number
  ceding_commission_rate: number
  color_index: number
  has_projection: boolean
  has_reporting: boolean
  term_sheet_raw: ScTermSheetRow[]
  deal_file_name?: string | null
}

export interface ScUnitParticipation {
  deal_id: string
  reinsurer_id: string
  reinsurer_name: string
  short_name: string
  panel_order: number
  participation_pct: number
  treaty_pct: number | null
  edited: boolean
  note: string
  initial_reinsured_limit: number
}

export interface ScQuarterlyFact {
  deal_id: string
  period: string
  year: number
  quarter: number
  insured_limit: number
  payment_date: string | null
  gross_premium: number
  gross_losses: number
  gross_recoveries: number
  gross_reserves: number
}

export interface ScQuarterlyFactByRe {
  deal_id: string
  reinsurer_id: string
  period: string
  year: number
  quarter: number
  participation_pct: number
  reinsured_limit: number
  ri_premium: number
  ceding_commission: number
  ceded_losses: number
  ceded_recoveries: number
  ceded_reserves: number
  net_ceded_premium: number
}

export interface ScExpectedCashflow {
  deal_id: string
  vintage_id: string
  is_current: boolean
  is_synthetic: boolean
  period: string
  year: number
  quarter: number
  expected_limit_eop: number | null
  expected_premium: number
  expected_loss: number
  expected_recovery: number
}

export interface ScReinsurer {
  reinsurer_id: string
  name: string
  short_name: string
  panel_order: number
  active: boolean
  deal_count: number
}

export interface ScVintage {
  vintage_id: string
  label: string
  as_of_date: string
  is_current: boolean
  deal_count: number
}

export interface ScProgramNode {
  program: string
  transaction_ids: string[]
}

export interface ScYearNode {
  year: number
  programs: ScProgramNode[]
}

export interface ScClientNode {
  client: string
  client_type: string
  years: ScYearNode[]
}

export interface ScTransactionModel {
  id: string
  client: string
  client_type: string
  year: number
  program: string
  name: string
  short_name: string
  structure: string
  basis: string | null
  reporting_level: string
  is_treaty: boolean
  effective_date: string
  end_date: string | null
  legal_final_date: string | null
  currency: string
  limit: number
  limit_original_ccy?: number | null
  fx_rate_to_usd?: number
  brokerage_rate: number
  cession_pct: number
  ceding_commission_rate: number
  status: string
  recognition_method: string
  placed_share: number
  panel: ScParticipation[]
  deal_ids: string[]
  has_projection: boolean
  has_reporting: boolean
  color_index: number
  notes: string
  comments: string
}

export interface ScDealScenario {
  deal_id: string
  scenario: string
  return_period_years: number | null
  lifetime_premium: number
  lifetime_loss_dcc: number
  is_synthetic: boolean
}

export interface ScModel {
  transactions: ScTransactionModel[]
  units: ScUnit[]
  unit_participations: ScUnitParticipation[]
  quarterly_facts: ScQuarterlyFact[]
  quarterly_facts_by_re: ScQuarterlyFactByRe[]
  expected_cashflows: ScExpectedCashflow[]
  transaction_cashflows: ScTransactionCashflow[]
  deal_scenarios: ScDealScenario[]
  reinsurers: ScReinsurer[]
  vintages: ScVintage[]
  hierarchy: ScClientNode[]
  settings: ScSettings
  meta: {
    transaction_count: number
    deal_count: number
    reporting_row_count: number
  }
}

export type ScCurrencyUnit = 'M' | 'USD'

export interface ScScope {
  client: string | null
  year: number | null
  program: string | null
  transactionIds: string[] | null
  asOf: string
  viewer: string
  vintage: string | null
  currencyUnit: ScCurrencyUnit
}

export interface ScDealKpis {
  deal: ScUnit
  status: string
  share: number
  initial_reinsured_limit: number
  reinsured_limit_remaining: number
  insured_limit: number
  ri_premium: number
  ceding_commission: number
  ceded_losses: number
  ceded_recoveries: number
  ceded_reserves: number
  net_ceded_premium: number
  loss_ratio: number | null
  exp_ri_premium_lifetime: number
  exp_ri_premium_to_date: number
  exp_ceded_losses_to_date: number
  exp_reinsured_limit_at_asof: number | null
  exp_ceded_losses_lifetime: number
  premium_ae: number | null
  premium_variance: number
  limit_ae: number | null
  loss_ae: number | null
  status_label?: string
  first_period: string | null
  last_period: string | null
  has_projection: boolean
}

export interface ScPortfolioKpis {
  deal_count: number
  reported_count: number
  status: string
  insured_limit: number
  initial_reinsured_limit: number
  reinsured_limit_remaining: number
  ri_premium: number
  ceding_commission: number
  ceded_losses: number
  ceded_recoveries: number
  ceded_reserves: number
  net_ceded_premium: number
  loss_ratio: number | null
  exp_ri_premium_lifetime: number
  exp_ri_premium_to_date: number
  exp_ceded_losses_to_date: number
  exp_reinsured_limit_at_asof: number | null
  exp_ceded_losses_lifetime: number
  premium_ae: number | null
  premium_variance: number
  limit_ae: number | null
  loss_ae: number | null
  transactions: ScTransactionKpis[]
  transaction_count: number
}

export interface ScTransactionKpis extends ScPortfolioKpis {
  transaction: ScTransactionModel
  share: number
  first_period: string | null
  last_period: string | null
  has_projection: boolean
  deals: ScDealKpis[]
}

export interface ScPeriodRow {
  period: string
  year: number
  quarter: number
  is_actual: boolean
  exp_premium: number
  exp_limit_eop: number
  exp_loss: number
  exp_ri_premium: number
  exp_reinsured_limit: number
  exp_ceded_losses: number
  exp_ceding_commission: number
  exp_net_ceded_premium: number
  exp_ri_premium_to_date: number
  exp_ceded_losses_to_date: number
  exp_ceding_commission_to_date: number
  exp_net_ceded_premium_to_date: number
  act_premium: number
  act_limit_eop: number
  act_loss: number
  act_ri_premium: number
  act_reinsured_limit: number
  act_ceded_losses: number
  act_ceding_commission: number
  act_net_ceded_premium: number
  by_deal: Record<
    string,
    {
      exp_ri_premium?: number
      act_ri_premium?: number
      exp_reinsured_limit?: number
      act_reinsured_limit?: number
      exp_net_ceded_premium?: number
      act_net_ceded_premium?: number
      exp_ceded_losses?: number
      act_ceded_losses?: number
      exp_ceding_commission?: number
      act_ceding_commission?: number
    }
  >
}

export const ADMIN_VIEWER = 'LOCKTON_RE'

// ---- Revenue: settings (recognition rules, pipeline stages) ----

export interface ScRecognitionRule {
  id: string
  label: string
  kind: 'treaty_year' | 'as_cash' | 'pull_forward'
  year1?: number[]
  later?: number[]
  pull?: number
  pull_at?: number
  builtin: boolean
  description?: string
}

export interface ScPipelineStage {
  id: string
  label: string
  probability: number
  system?: 'bound'
}

export interface ScSettings {
  recognition_rules: ScRecognitionRule[]
  pipeline_stages: ScPipelineStage[]
}

// ---- Revenue: treaty-level (100%-basis) projection, flattened ----

export interface ScTransactionCashflow {
  transaction_id: string
  vintage_id: string
  is_current: boolean
  period: string
  year: number
  quarter: number
  expected_premium: number
}

// ---- Revenue: scope / UI filter state ----

export type ScRevenueWeighting = 'none' | 'probability'
export type ScRevenueUnwritten = 'include' | 'exclude'
export type ScRevenueGranularity = 'fy' | 'quarter'
export type ScRevenueMeasureId = 'cash' | 'recognized' | 'actual'
export type ScRevenueDimensionId =
  | 'transaction'
  | 'program'
  | 'year'
  | 'client'
  | 'client_type'
  | 'structure'
  | 'basis'
  | 'status'
  | 'method'

export interface ScRevenueScope {
  client: string | null
  year: number | null
  program: string | null
  statuses: string[] | null
  asOf: string
  vintage: string | null
  horizon: number
  weighting: ScRevenueWeighting
  unwritten: ScRevenueUnwritten
  compareVintage: string | null
  currencyUnit: 'K' | 'M' | 'USD'
  granularity: ScRevenueGranularity
  measure: ScRevenueMeasureId
  groupBy: ScRevenueDimensionId | 'none'
}

// ---- Revenue: engine output shapes ----

export interface ScRevenueFlow {
  period: string
  year: number
  quarter: number
  cash: number
  /** The portion of `cash` that is bordereau-reported, not forecast; 0 when is_actual is false. */
  actual_cash: number
  is_actual: boolean
}

export interface ScRevenueByFy {
  fiscal_year: number
  cash: number
  recognized: number
}

export interface ScRevenueSummary {
  ultimate: number
  wal_years: number | null
  cash_by_fy: ScRevenueByFy[]
  cash_to_date: number
  cash_through_fy: number
  recognized_through_fy: number
  deferred_balance: number
  fy_as_of: number
  fy_min: number
  fy_max: number
  has_actual: boolean
}

export interface ScRevenueAveRow {
  period: string
  actual: number
  projected: number
}

export interface ScRevenueAve {
  rows: ScRevenueAveRow[]
  actual: number
  projected: number
  variance: number
  ratio: number | null
  status: 'reported' | 'expected_not_reported' | 'not_yet_due'
}

export interface ScDealRevenue {
  deal_id: string
  vintage_used: string
  flows: ScRevenueFlow[]
  comparisonFlows: ScRevenueFlow[]
  summary: ScRevenueSummary
  ave: ScRevenueAve
}

export interface ScTransactionRevenue {
  transaction_id: string
  is_treaty: boolean
  flows: ScRevenueFlow[]
  comparisonFlows: ScRevenueFlow[]
  summary: ScRevenueSummary
  ave: ScRevenueAve
  written?: { deals: ScDealRevenue[]; ultimate: number }
  remainder?: { flows: ScRevenueFlow[]; ultimate: number; scale: number }
  written_share?: number | null
  unwritten_ultimate?: number
}

export interface ScTreatyRow {
  transaction: ScTransactionModel
  projected_lifetime: number | null
  deal_count: number
  written_lifetime: number
  written_pct: number | null
  unwritten_remainder: number
  cash_to_date: number
  projection_vintage_label: string
}

export interface ScPortfolioRow {
  transaction: ScTransactionModel
  revenue: ScTransactionRevenue
}

export interface ScPortfolioGroup {
  key: string
  name: string
  rows: ScPortfolioRow[]
}

export interface ScPortfolioResult {
  rows: ScPortfolioRow[]
  fy_columns: number[]
  tail_fy: number | null
  by: Record<ScRevenueDimensionId, ScPortfolioGroup[]>
}

export interface ScRevenueLongRow {
  transaction_id: string
  fiscal_year: number
  cash: number
  recognized: number
  unwritten_cash: number
}

// ---- Revenue: registries ----

export interface ScRevenueMeasureByFy {
  fiscal_year: number
  value: number
}

export interface ScRevenueMeasure {
  id: ScRevenueMeasureId
  label: string
  signed: boolean
  byFy(tr: ScTransactionRevenue): ScRevenueMeasureByFy[]
  total(tr: ScTransactionRevenue): number
}

export interface ScRevenueDimension {
  id: ScRevenueDimensionId
  label: string
  key(t: ScTransactionModel): string
  name(key: string, t: ScTransactionModel): string
}
